import type { Asset } from '../types'
import { getStorageUrl } from './api'
import { isVideoAsset } from './assetUtils'
import { getEffectiveProxyUri } from './proxyResolution'

export interface VideoPlaybackSourceInput {
  videoId: string | null | undefined
  proxyUri?: string | null
  uri?: string | null
}

export interface VideoPlaybackSource {
  videoId: string
  uri: string
  url: string
  key: string
}

const VIDEO_PLAYBACK_SOURCE_KEY_SEPARATOR = '\u0000'

export function getVideoPlaybackSourceKey(videoId: string, videoUrl: string): string {
  return `${videoId}${VIDEO_PLAYBACK_SOURCE_KEY_SEPARATOR}${videoUrl}`
}

export function createVideoPlaybackSourceFromUrl(videoId: string, url: string, uri = url): VideoPlaybackSource {
  return {
    videoId,
    uri,
    url,
    key: getVideoPlaybackSourceKey(videoId, url),
  }
}

/**
 * Resolve the canonical source tuple for video playback.
 *
 * Keep proxy-resolution selection and storage URL conversion together so
 * playback callers don't rebuild this identity differently across surfaces.
 */
export function resolveVideoPlaybackSource(input: VideoPlaybackSourceInput): VideoPlaybackSource | null {
  if (!input.videoId) return null

  const uri = getEffectiveProxyUri(input.proxyUri ?? undefined, input.uri ?? undefined)
  if (!uri) return null

  const url = getStorageUrl(uri)

  return {
    videoId: input.videoId,
    uri,
    url,
    key: getVideoPlaybackSourceKey(input.videoId, url),
  }
}

export function resolveAssetVideoPlaybackSource(asset: Asset | undefined | null): VideoPlaybackSource | null {
  if (!asset || !isVideoAsset(asset)) return null
  return resolveVideoPlaybackSource({
    videoId: asset.id,
    proxyUri: asset.proxyUri,
    uri: asset.uri,
  })
}
